import type {
  MarketQuote, Order, OrderLine, OrderStatus, Party, Product, PurchaseTask,
  Quantity, Route, Shipment, StopStatus, TimeWindow, Unit,
} from '@prody/shared';
import { DEMAND_ORDER_STATUSES, buildSupplierDemand, money } from '@prody/shared';
import type {
  AssignSupplierInput, NewOrderInput, OrderFilters, ProdyApi,
} from './api';
import { BUSINESSES, SESSION } from './seed';

/* ==================================================================
   THE LIVE ADAPTER.

   The old admin's backend is still the system of record for orders:

     GET /api/orders/admin-limit-legacy?tradeName=TPO&…

   This module reads that endpoint (plus /api/routes and the supplier
   directory) and re-expresses everything in the Prody domain, so the
   order book, the grouping board, the supplier sheets and the
   delivery list are all derived from ONE live feed — no second copy,
   no per-tab backend.

   Performance rules, because a legacy order weighs ~40 KB of JSON:
     - every URL is fetched once per TTL window and shared between
       tabs (in-flight dedupe + a 20 s cache);
     - day views are filtered server-side (simpleDeliveryDate);
     - raw payloads are mapped once and dropped — only domain objects
       are retained;
     - anything the feed cannot answer falls back to the seed API, so
       a dead network degrades to the demo instead of a spinner.

   Writes are LOCAL overlays for now: edits, status moves, supplier
   assignments and new orders apply to the mapped data instantly and
   survive the session, but nothing is posted back to production
   until those endpoints are wired deliberately.
   ================================================================== */

const API_BASE = 'https://king-prawn-app-ugsdt.ondigitalocean.app';
const LEGACY_BASE = 'https://tpomobi.shop';
const TENANT = 'TPO';
const CURRENCY = 'EUR';

const PRIMARY_BIZ = BUSINESSES[0]!.id as string;

/* ---------------- Fetch layer ---------------- */

const TTL_MS = 20_000;
const TIMEOUT_MS = 12_000;

interface CacheEntry {
  at: number;
  promise: Promise<unknown>;
}

const cache = new Map<string, CacheEntry>();

const fetchJson = <T>(url: string): Promise<T> => {
  const now = Date.now();
  const hit = cache.get(url);
  if (hit && now - hit.at < TTL_MS) return hit.promise as Promise<T>;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const promise = fetch(url, { signal: controller.signal })
    .then((res) => {
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json() as Promise<T>;
    })
    .catch((err) => {
      // A failed fetch must not be cached as success for 20 s.
      cache.delete(url);
      throw err;
    })
    .finally(() => clearTimeout(timer));

  cache.set(url, { at: now, promise });
  return promise;
};

/* ---------------- Legacy shapes (loose on purpose) ---------------- */

interface LegacyItem {
  _id: string;
  sku?: string;
  name?: string;
  quantity?: number;
  quantityOrder?: number;
  currientRequest?: string;
  unit?: string;
  currentUnit?: string;
  marketPriceCP?: number;
  ourPriceCP?: number;
  ourPrice?: number;
  IVA?: number;
  IVACP?: number;
  minQt?: number;
  proveedor?: string;
  status?: { name?: string } | null;
}

interface LegacyOrder {
  _id: string;
  particalId?: string;
  idTucomida?: string;
  date?: number;
  createdAt?: string;
  address?: string;
  tradeName?: string;
  type?: string;
  realType?: string;
  status?: { name?: string; color?: string } | null;
  items?: LegacyItem[];
  deliveryDate?: string | string[];
  deliveryTime?: string;
  deliveryPrice?: number;
  paidValue?: number;
  paymentType?: string | null;
  terminalName?: string;
  orderDeliveryType?: string;
  description?: string;
  innerDescription?: string;
  logistic?: { routeId?: string | null; position?: number } | null;
  userAcc?: {
    name?: string;
    phone?: string;
    comArdress?: string;
    mapboxFormattedAddress?: string | null;
    location?: string;
  } | null;
}

interface LegacyRoute {
  _id: string;
  name?: string;
  color?: string;
  vehicle?: string;
  driver?: { name?: string } | null;
}

interface LegacySupplier {
  _id?: string;
  name?: string;
  link?: string;
  tradeName?: string;
  enabled?: boolean;
}

/* ---------------- Mapping tables ---------------- */

const ORDER_STATUS: Record<string, OrderStatus> = {
  Picking: 'picking',
  'Listo parcial': 'picking',
  'Listo Parcial': 'picking',
  Listo: 'ready',
  'Listo Cargar': 'ready',
  'De Camino': 'dispatched',
  Entregado: 'delivered',
  Facturado: 'invoiced',
  Emitido: 'invoiced',
  'Emitido P': 'invoiced',
  'New Factura': 'invoiced',
  Cancelado: 'cancelled',
};

const mapStatus = (name: string | undefined): OrderStatus =>
  (name && ORDER_STATUS[name]) || 'placed';

const STOP_FROM_ORDER: Partial<Record<OrderStatus, StopStatus>> = {
  dispatched: 'en-route',
  delivered: 'delivered',
  invoiced: 'delivered',
  cancelled: 'skipped',
};

const mapChannel = (type?: string, realType?: string): Order['channel'] => {
  const key = `${realType ?? ''}|${type ?? ''}`;
  if (key.includes('whatsapp')) return 'whatsapp';
  if (key.includes('telegram')) return 'telegram';
  if (key.includes('phone')) return 'phone';
  return 'portal';
};

/*
 * Units in the legacy feed need care, because three different things
 * look alike:
 *
 *   item.unit          "kg, und, caja"  — the units this product CAN be
 *                                        sold in. The FIRST one is the
 *                                        unit its price is quoted in.
 *   item.quantity      0.99             — the billable amount, already
 *                                        expressed in that price unit.
 *   item.currientRequest "3 und"        — what the customer asked for,
 *                                        in whatever unit they think in.
 *
 * So the line's unit comes from the price unit, never from the request:
 * three peppers is 0.99 kg at €2.99/kg, and rendering that as "1 pc"
 * both loses the weight and misprices the line.
 */
const UNIT_MAP: Record<string, Unit> = {
  kg: 'kg', gr: 'g', g: 'g', lt: 'l', l: 'l', ml: 'ml', cl: 'ml',
  caja: 'box', box: 'box', saco: 'box', palet: 'pallet', pallet: 'pallet',
  // Spanish packaging words that all mean "one of these": a bunch, a
  // tub, a bottle, a bag, a tray, a packet, a tin, a jug, a tub.
  und: 'pc', ud: 'pc', uds: 'pc', man: 'pc', tarr: 'pc', tarro: 'pc',
  bote: 'pc', bolsa: 'pc', band: 'pc', paq: 'pc', lata: 'pc',
  garrafa: 'pc', cubo: 'pc', pieza: 'pc', pz: 'pc',
};

const priceUnitOf = (item: LegacyItem): Unit => {
  const raw = (item.currentUnit || item.unit || '').split(',')[0]!.trim().toLowerCase();
  return UNIT_MAP[raw] ?? 'pc';
};

/** The customer's own words for what they wanted — "3 und", "16 kg". */
const requestOf = (item: LegacyItem): string | null => {
  const raw = item.currientRequest?.trim();
  return raw && raw.length > 0 ? raw : null;
};

const TAX_KEY_BY_PCT: Record<number, string> = {
  21: 'standard', 10: 'reduced', 4: 'super-reduced', 0: 'zero',
  5: 'iva-5', 16: 'iva-16',
};

const taxKey = (item: LegacyItem): string =>
  TAX_KEY_BY_PCT[item.IVACP ?? item.IVA ?? 10] ?? 'reduced';

const m = (major: number | undefined) => money(Math.round((major ?? 0) * 100), CURRENCY);

/** "30.07.2026, PLM ☀️" → { date, slot }. Emoji and spacing vary; regex, not split. */
const parseDelivery = (raw: LegacyOrder): { date: string; slot: 'PLM' | 'PLT' | null } => {
  const s = Array.isArray(raw.deliveryDate) ? raw.deliveryDate[0] ?? '' : raw.deliveryDate ?? '';
  const match = /(\d{2})\.(\d{2})\.(\d{4})/.exec(s);
  const date = match
    ? `${match[3]}-${match[2]}-${match[1]}`
    : new Date(raw.date ?? Date.now()).toISOString().slice(0, 10);
  const slotSource = `${s} ${raw.deliveryTime ?? ''}`;
  const slot = slotSource.includes('PLM') ? 'PLM' : slotSource.includes('PLT') ? 'PLT' : null;
  return { date, slot };
};

const SLOT_WINDOW: Record<'PLM' | 'PLT', TimeWindow> = {
  PLM: { from: '07:00', to: '14:00' },
  PLT: { from: '14:00', to: '22:00' },
};

const toLegacyDate = (plain: string): string => {
  const [y, mo, d] = plain.split('-');
  return `${d}.${mo}.${y}`;
};

const slug = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-');

/** Item states that mean "this line is picked / confirmed". */
const PICKED_ITEM_STATUSES = new Set(['Ready', 'READY', 'Listo', 'CONFIRMAR', 'Confirmar']);

/* ---------------- The adapter ---------------- */

export const createLegacyApi = (fallback: ProdyApi): ProdyApi => {
  /* Derived catalogues, grown from every payload that passes through. */
  const mappedById = new Map<string, Order>();
  const productCatalog = new Map<string, Product>();
  const priceBook = new Map<string, { sell: number; cost: number }>();
  const customerCatalog = new Map<string, Party>();
  const routeCatalog = new Map<string, { name: string; vehicle: string | null; driver: string | null }>();
  let supplierCatalog: Party[] | null = null;

  /* Local overlays — the session's own writes. */
  const orderPatches = new Map<string, Partial<Order>>();
  const createdOrders: Order[] = [];
  const shipmentPatches = new Map<string, Partial<Shipment>>();
  const quotePatches = new Map<string, Partial<MarketQuote>>();
  let localTasks: PurchaseTask[] = [];
  let createdSeq = 0;

  const audit = (iso: string) => ({
    createdAt: iso, createdBy: null, updatedAt: iso, updatedBy: null, deletedAt: null,
  });

  /* ---------- Product & customer registration ---------- */

  const registerProduct = (item: LegacyItem): void => {
    const id = item._id;
    if (!id || productCatalog.has(id)) return;
    const name = item.name ?? item.sku ?? id;
    const unit = priceUnitOf(item);
    productCatalog.set(id, {
      ...audit(new Date().toISOString()),
      id: id as Product['id'],
      businessId: PRIMARY_BIZ as Product['businessId'],
      custom: {},
      sku: item.sku ?? id.slice(-6),
      barcode: null,
      name: { en: name, es: name, ru: name },
      description: {},
      categoryId: null,
      unitMode: unit === 'kg' || unit === 'g' ? 'weight' : unit === 'l' || unit === 'ml' ? 'volume' : 'each',
      salesUnit: unit,
      minQuantity: item.minQt ?? 1,
      quantityStep: unit === 'kg' ? 0.5 : 1,
      handlingClass: 'neutral',
      taxRateKey: taxKey(item),
      traits: {},
      defaultSupplierId: null,
      substituteIds: [] as never,
      imageUrl: null,
      tags: [],
      status: 'active',
    });
    priceBook.set(id, { sell: item.marketPriceCP ?? 0, cost: item.ourPriceCP ?? item.ourPrice ?? 0 });
  };

  const registerCustomer = (raw: LegacyOrder): string => {
    const code = raw.tradeName || 'UNKNOWN';
    const id = `cli_${code}`;
    if (!customerCatalog.has(id)) {
      const address = raw.userAcc?.mapboxFormattedAddress || raw.userAcc?.comArdress || '';
      customerCatalog.set(id, {
        ...audit(new Date().toISOString()),
        id: id as Party['id'],
        businessId: PRIMARY_BIZ as Party['businessId'],
        custom: {},
        kind: 'company',
        roles: ['customer'],
        code,
        name: raw.address || raw.userAcc?.name || code,
        legalName: null,
        taxId: null,
        locale: 'es',
        channels: raw.userAcc?.phone
          ? [{ kind: 'phone', address: `+${raw.userAcc.phone.replace(/^\+/, '')}`, label: null, primary: true, verifiedAt: null, optedOut: false }]
          : [],
        people: [],
        addresses: address
          ? [{ id: 'a1', label: 'Local', line1: address, line2: null, city: '', postcode: '', region: null, country: 'ES', lat: null, lng: null, accessNotes: null }]
          : [],
        deliveryPreferences: [],
        paymentTerms: { netDays: 0, creditLimitMinor: null, blockOnOverLimit: false, method: 'transfer' },
        priceListId: null,
        ownerId: null,
        referredByPartyId: null,
        supplier: null,
        ambassador: null,
        tags: [],
        notes: null,
        status: 'active',
      });
    }
    return id;
  };

  /* ---------- Order mapping ---------- */

  const mapOrder = (raw: LegacyOrder): Order => {
    const { date, slot } = parseDelivery(raw);
    const placedIso = new Date(raw.date ?? Date.now()).toISOString();
    const customerId = registerCustomer(raw);
    const customer = customerCatalog.get(customerId)!;
    const lineCosts: Record<string, number> = {};
    const lineRequests: Record<string, string> = {};

    const lines: OrderLine[] = (raw.items ?? []).map((item, i) => {
      registerProduct(item);
      const unit = priceUnitOf(item);
      const soldQty = item.quantity ?? 0;
      const orderedQty = item.quantityOrder ?? soldQty;
      const ordered: Quantity = { value: orderedQty, unit };
      const picked = PICKED_ITEM_STATUSES.has(item.status?.name ?? '')
        ? { value: soldQty, unit }
        : null;
      const lineId = `${raw._id}:${item._id}:${i}`;
      lineCosts[lineId] = Math.round((item.ourPriceCP ?? item.ourPrice ?? 0) * 100);
      const request = requestOf(item);
      if (request) lineRequests[lineId] = request;
      return {
        id: lineId as OrderLine['id'],
        productId: item._id as OrderLine['productId'],
        productName: item.name ?? item.sku ?? item._id,
        sku: item.sku ?? item._id.slice(-6),
        ordered,
        picked,
        invoiced: null,
        unitPrice: m(item.marketPriceCP),
        priceSource: 'market',
        discountBps: 0,
        taxRateKey: taxKey(item),
        supplierId: (item.proveedor ? `sup_${slug(item.proveedor)}` : null) as OrderLine['supplierId'],
        supplierName: item.proveedor || null,
        shortfall: null,
        substitution: null,
        note: null,
        sortOrder: i + 1,
      };
    });

    return {
      ...audit(placedIso),
      id: raw._id as Order['id'],
      businessId: PRIMARY_BIZ as Order['businessId'],
      custom: {
        legacyStatus: raw.status?.name ?? null,
        paymentType: raw.paymentType ?? null,
        terminal: raw.terminalName ?? null,
        paidMinor: Math.round((raw.paidValue ?? 0) * 100),
        tradeName: raw.tradeName ?? null,
        contactName: raw.userAcc?.name || null,
        phone: raw.userAcc?.phone ? `+${raw.userAcc.phone.replace(/^\+/, '')}` : null,
        routeId: raw.logistic?.routeId ?? null,
        routePosition: raw.logistic?.position ?? 0,
        addressLabel:
          raw.userAcc?.mapboxFormattedAddress || raw.userAcc?.comArdress || raw.address || '',
        lineCosts,
        lineRequests,
      },
      number: (raw.particalId || raw.idTucomida || raw._id.slice(-6)).toUpperCase(),
      status: mapStatus(raw.status?.name),
      customerId: customerId as Order['customerId'],
      customerName: customer.name,
      channel: mapChannel(raw.type, raw.realType),
      placedAt: placedIso,
      deliveryDate: date,
      deliveryWindow: slot ? SLOT_WINDOW[slot] : null,
      deliveryAddressId: null,
      currency: CURRENCY,
      lines,
      documentDiscountBps: 0,
      deliveryFee: raw.deliveryPrice ? m(raw.deliveryPrice) : null,
      assignedToId: null,
      shipmentId: `shp_${raw._id}`,
      invoiceId: null,
      customerNote: raw.description || null,
      internalNote: raw.innerDescription || null,
      tags: slot ? [slot] : [],
    };
  };

  const withPatch = (order: Order): Order => {
    const patch = orderPatches.get(order.id as string);
    return patch ? { ...order, ...patch } : order;
  };

  /* ---------- Order feed ---------- */

  const fetchOrders = async (filters: OrderFilters = {}): Promise<Order[]> => {
    // The caller's limit wins (the list has a rows switch, like the old
    // admin); day views default high because the server filters them.
    const limit = Math.min(filters.limit ?? (filters.deliveryDate ? 300 : 100), 500);
    const params = new URLSearchParams({
      tradeName: TENANT,
      limit: String(limit),
      search: filters.search ?? '',
      simpleDeliveryDate: filters.deliveryDate ? toLegacyDate(filters.deliveryDate) : '',
    });
    const raws = await fetchJson<LegacyOrder[]>(
      `${API_BASE}/api/orders/admin-limit-legacy?${params}`,
    );
    const mapped = raws.map(mapOrder);
    for (const order of mapped) mappedById.set(order.id as string, order);
    return mapped.map(withPatch);
  };

  /** One warm pass so catalogues exist before products/parties are asked for. */
  const ensureFeed = () => fetchOrders({ limit: 60 });

  const applyFilters = (rows: Order[], filters: OrderFilters): Order[] => {
    let out = rows;
    if (filters.deliveryDate) out = out.filter((o) => o.deliveryDate === filters.deliveryDate);
    if (filters.statuses?.length) out = out.filter((o) => filters.statuses!.includes(o.status));
    if (filters.customerId) out = out.filter((o) => o.customerId === filters.customerId);
    if (filters.shortOnly) out = out.filter((o) => o.lines.some((l) => l.shortfall));
    if (filters.search) {
      const q = filters.search.trim().toLowerCase();
      out = out.filter((o) =>
        [o.number, o.customerName, ...o.lines.map((l) => l.productName)]
          .some((s) => s.toLowerCase().includes(q)),
      );
    }
    return out;
  };

  /* ---------- Suppliers directory ---------- */

  const fetchSuppliers = async (): Promise<Party[]> => {
    if (supplierCatalog) return supplierCatalog;
    try {
      const raws = await fetchJson<LegacySupplier[]>(`${LEGACY_BASE}/getSuppliersLink`);
      // Only identity comes across. The endpoint also carries site
      // credentials; those are deliberately never read or stored here.
      supplierCatalog = raws
        .filter((s) => s.name)
        .map((s) => {
          const code = (s.tradeName ?? s.link ?? s.name!).split(',')[0]!.trim();
          return {
            ...audit(new Date().toISOString()),
            id: `sup_${slug(code || s.name!)}` as Party['id'],
            businessId: PRIMARY_BIZ as Party['businessId'],
            custom: {},
            kind: 'company' as const,
            roles: ['supplier' as const],
            code: code.toUpperCase(),
            name: s.name!,
            legalName: null,
            taxId: null,
            locale: 'es' as const,
            channels: [],
            people: [],
            addresses: [],
            deliveryPreferences: [],
            paymentTerms: { netDays: 0, creditLimitMinor: null, blockOnOverLimit: false, method: 'transfer' as const },
            priceListId: null,
            ownerId: null,
            referredByPartyId: null,
            supplier: { sourceKind: 'other' as const, runDays: [], standCode: null, orderCutoff: null, leadTimeDays: 0 },
            ambassador: null,
            tags: [],
            notes: null,
            status: 'active' as const,
          };
        });
    } catch {
      supplierCatalog = [];
    }
    return supplierCatalog;
  };

  /* ---------- Routes ---------- */

  const fetchRoutes = async (): Promise<void> => {
    try {
      const raws = await fetchJson<LegacyRoute[]>(`${API_BASE}/api/routes`);
      for (const r of raws) {
        routeCatalog.set(r._id, {
          name: r.name ?? r._id.slice(-6),
          vehicle: r.vehicle ? r.vehicle.slice(-4).toUpperCase() : null,
          driver: r.driver?.name ?? null,
        });
      }
    } catch {
      /* route names degrade to ids; the sheet still works */
    }
  };

  const shipmentFromOrder = (order: Order): Shipment => {
    const custom = order.custom as Record<string, unknown>;
    const base: Shipment = {
      ...audit(order.updatedAt),
      id: `shp_${order.id}` as Shipment['id'],
      businessId: PRIMARY_BIZ as Shipment['businessId'],
      custom: {},
      number: order.number,
      routeId: (custom['routeId'] as string | null) ?? null,
      customerId: order.customerId,
      customerName: order.customerName,
      addressId: null,
      addressLabel: (custom['addressLabel'] as string) || order.customerName,
      orderIds: [order.id],
      date: order.deliveryDate,
      window: order.deliveryWindow,
      sequence: (custom['routePosition'] as number) ?? 0,
      status: STOP_FROM_ORDER[order.status] ?? 'pending',
      etaAt: null,
      arrivedAt: null,
      completedAt: null,
      crateCount: order.lines.length,
      cratesReturned: 0,
      proof: null,
      failureReason: null,
      note: order.customerNote,
    };
    const patch = shipmentPatches.get(base.id as string);
    return patch ? { ...base, ...patch } : base;
  };

  /* ---------- Purchase task derivation ---------- */

  const buildTasks = (orders: Order[], tradingDay: string): PurchaseTask[] => {
    const costFor = new Map<string, number>();
    for (const order of orders) {
      const costs = (order.custom as Record<string, unknown>)['lineCosts'] as
        | Record<string, number>
        | undefined;
      for (const line of order.lines) {
        const cost = costs?.[line.id as string];
        if (cost && !costFor.has(line.productId as string)) {
          costFor.set(line.productId as string, cost);
        }
      }
    }
    const demand = buildSupplierDemand(orders, (line) =>
      line.supplierId ? { id: line.supplierId as string, name: line.supplierName ?? '—' } : null,
    );
    const now = new Date().toISOString();
    return demand.map((row, i) => ({
      ...audit(now),
      id: `pt_live_${tradingDay}_${i + 1}` as PurchaseTask['id'],
      businessId: PRIMARY_BIZ as PurchaseTask['businessId'],
      custom: {},
      shoppingListId: `sl_${tradingDay}`,
      productId: row.productId as PurchaseTask['productId'],
      productName: row.productName,
      sku: row.sku,
      supplierId: (row.supplierId ?? null) as PurchaseTask['supplierId'],
      supplierName: row.supplierName,
      standCode: null,
      required: row.total,
      buffer: null,
      bought: null,
      expectedCost: costFor.has(row.productId)
        ? money(costFor.get(row.productId)!, CURRENCY)
        : null,
      actualCost: null,
      status: 'todo',
      demandBreakdown: row.orders.map((o) => ({
        orderId: o.orderId as PurchaseTask['demandBreakdown'][number]['orderId'],
        orderNumber: o.orderNumber,
        customerName: o.customerName,
        quantity: o.quantity,
      })),
      note: null,
      completedAt: null,
      sortOrder: i + 1,
    }));
  };

  /* ---------- The ProdyApi surface ---------- */

  const isPrimary = (businessId: string) => businessId === PRIMARY_BIZ;

  return {
    getSession: () => fallback.getSession(),

    listOrders: async (businessId, filters = {}) => {
      if (!isPrimary(businessId)) return fallback.listOrders(businessId, filters);
      try {
        const live = await fetchOrders(filters);
        const local = applyFilters(createdOrders.map(withPatch), filters);
        return [...local, ...applyFilters(live, filters)];
      } catch (err) {
        console.warn('[legacy] listOrders fell back to seed:', err);
        return fallback.listOrders(businessId, filters);
      }
    },

    getOrder: async (businessId, orderId) => {
      if (!isPrimary(businessId)) return fallback.getOrder(businessId, orderId);
      const created = createdOrders.find((o) => o.id === orderId);
      if (created) return withPatch(created);
      if (!mappedById.has(orderId)) {
        try {
          await ensureFeed();
        } catch {
          return fallback.getOrder(businessId, orderId);
        }
      }
      const found = mappedById.get(orderId);
      return found ? withPatch(found) : null;
    },

    createOrder: async (businessId, input: NewOrderInput) => {
      if (!isPrimary(businessId)) return fallback.createOrder(businessId, input);
      const now = new Date().toISOString();
      const customer =
        customerCatalog.get(input.customerId) ??
        (await fetchSuppliers()).find((p) => p.id === input.customerId);
      const number = `${String(new Date().getUTCFullYear() % 100)}-9${String(++createdSeq).padStart(3, '0')}`;
      const order: Order = {
        ...audit(now),
        id: `ord_new_${createdSeq}` as Order['id'],
        businessId: businessId as Order['businessId'],
        custom: { local: true, lineCosts: {} },
        number,
        status: input.status,
        customerId: input.customerId as Order['customerId'],
        customerName: customer?.name ?? input.customerId,
        channel: input.channel,
        placedAt: input.status === 'placed' ? now : null,
        deliveryDate: input.deliveryDate,
        deliveryWindow: input.deliveryWindow,
        deliveryAddressId: null,
        currency: CURRENCY,
        lines: input.lines.map((row, i) => {
          const product = productCatalog.get(row.productId);
          return {
            id: `oln_new_${createdSeq}_${i}` as OrderLine['id'],
            productId: row.productId as OrderLine['productId'],
            productName: product?.name['en'] ?? row.productId,
            sku: product?.sku ?? row.productId,
            ordered: row.quantity,
            picked: null,
            invoiced: null,
            unitPrice: row.unitPrice,
            priceSource: row.priceSource,
            discountBps: 0,
            taxRateKey: product?.taxRateKey ?? 'reduced',
            supplierId: null,
            supplierName: null,
            shortfall: null,
            substitution: null,
            note: row.note ?? null,
            sortOrder: i + 1,
          };
        }),
        documentDiscountBps: 0,
        deliveryFee: null,
        assignedToId: SESSION.user.id,
        shipmentId: null,
        invoiceId: null,
        customerNote: input.customerNote ?? null,
        internalNote: input.internalNote ?? null,
        tags: [],
      };
      createdOrders.unshift(order);
      return structuredClone(order);
    },

    updateOrder: async (businessId, orderId, patch) => {
      if (!isPrimary(businessId)) return fallback.updateOrder(businessId, orderId, patch);
      const created = createdOrders.find((o) => o.id === orderId);
      const base = created ?? mappedById.get(orderId);
      if (!base) throw new Error('Order not found');
      const prev = orderPatches.get(orderId) ?? {};
      orderPatches.set(orderId, { ...prev, ...patch, updatedAt: new Date().toISOString() });
      return structuredClone(withPatch(base));
    },

    assignSupplier: async (businessId, input: AssignSupplierInput) => {
      if (!isPrimary(businessId)) return fallback.assignSupplier(businessId, input);
      // Make sure the day's orders are in the map before stamping them.
      try {
        await fetchOrders({ deliveryDate: input.deliveryDate });
      } catch {
        /* stamp whatever is cached */
      }
      const touched: Order[] = [];
      const candidates = [...mappedById.values(), ...createdOrders];
      for (const order of candidates) {
        const current = withPatch(order);
        if (current.deliveryDate !== input.deliveryDate) continue;
        if (!DEMAND_ORDER_STATUSES.includes(current.status)) continue;
        if (!current.lines.some((l) => l.productId === input.productId)) continue;
        const lines = current.lines.map((l) =>
          l.productId === input.productId
            ? {
                ...l,
                supplierId: (input.supplier?.id ?? null) as OrderLine['supplierId'],
                supplierName: input.supplier?.name ?? null,
              }
            : l,
        );
        const prev = orderPatches.get(current.id as string) ?? {};
        orderPatches.set(current.id as string, {
          ...prev,
          lines,
          updatedAt: new Date().toISOString(),
        });
        touched.push(withPatch(order));
      }
      return structuredClone(touched);
    },

    generatePurchaseTasks: async (businessId, tradingDay) => {
      if (!isPrimary(businessId)) return fallback.generatePurchaseTasks(businessId, tradingDay);
      const day = await fetchOrders({ deliveryDate: tradingDay });
      const local = createdOrders
        .map(withPatch)
        .filter((o) => o.deliveryDate === tradingDay);
      const demandOrders = [...day, ...local].filter((o) =>
        DEMAND_ORDER_STATUSES.includes(o.status),
      );
      localTasks = buildTasks(demandOrders, tradingDay);
      return structuredClone(localTasks);
    },

    listPurchaseTasks: async (businessId, tradingDay) => {
      if (!isPrimary(businessId)) return fallback.listPurchaseTasks(businessId, tradingDay);
      return structuredClone(localTasks);
    },

    updatePurchaseTask: async (businessId, taskId, patch) => {
      if (!isPrimary(businessId)) return fallback.updatePurchaseTask(businessId, taskId, patch);
      const index = localTasks.findIndex((t) => t.id === taskId);
      if (index < 0) throw new Error('Task not found');
      localTasks[index] = { ...localTasks[index]!, ...patch, updatedAt: new Date().toISOString() };
      return structuredClone(localTasks[index]!);
    },

    listQuotes: async (businessId, tradingDay) => {
      if (!isPrimary(businessId)) return fallback.listQuotes(businessId, tradingDay);
      try {
        await ensureFeed();
      } catch {
        return fallback.listQuotes(businessId, tradingDay);
      }
      const quotes: MarketQuote[] = [...priceBook.entries()].map(([productId, price]) => {
        const id = `mq_live_${productId}`;
        const base: MarketQuote = {
          ...audit(new Date().toISOString()),
          id,
          businessId: businessId as MarketQuote['businessId'],
          custom: {},
          productId: productId as MarketQuote['productId'],
          tradingDay,
          supplierId: null as MarketQuote['supplierId'],
          cost: m(price.cost),
          sell: m(price.sell),
          previousSell: null,
          availability: 'plenty',
          note: null,
        };
        const patch = quotePatches.get(id);
        return patch ? { ...base, ...patch } : base;
      });
      return quotes;
    },

    updateQuote: async (businessId, quoteId, patch) => {
      if (!isPrimary(businessId)) return fallback.updateQuote(businessId, quoteId, patch);
      const prev = quotePatches.get(quoteId) ?? {};
      quotePatches.set(quoteId, { ...prev, ...patch });
      const productId = quoteId.replace('mq_live_', '');
      const price = priceBook.get(productId);
      return {
        ...audit(new Date().toISOString()),
        id: quoteId,
        businessId: businessId as MarketQuote['businessId'],
        custom: {},
        productId: productId as MarketQuote['productId'],
        tradingDay: new Date().toISOString().slice(0, 10),
        supplierId: null as MarketQuote['supplierId'],
        cost: m(price?.cost ?? 0),
        sell: m(price?.sell ?? 0),
        previousSell: null,
        availability: 'plenty',
        note: null,
        ...prev,
        ...patch,
      } as MarketQuote;
    },

    listShipments: async (businessId, date) => {
      if (!isPrimary(businessId)) return fallback.listShipments(businessId, date);
      try {
        const [orders] = await Promise.all([fetchOrders({ deliveryDate: date }), fetchRoutes()]);
        const local = createdOrders
          .map(withPatch)
          .filter((o) => o.deliveryDate === date);
        return [...orders, ...local]
          .filter((o) => o.status !== 'cancelled')
          .map(shipmentFromOrder);
      } catch (err) {
        console.warn('[legacy] listShipments fell back to seed:', err);
        return fallback.listShipments(businessId, date);
      }
    },

    updateShipment: async (businessId, shipmentId, patch) => {
      if (!isPrimary(businessId)) return fallback.updateShipment(businessId, shipmentId, patch);
      const prev = shipmentPatches.get(shipmentId) ?? {};
      shipmentPatches.set(shipmentId, { ...prev, ...patch });
      const orderId = shipmentId.replace('shp_', '');
      const order = mappedById.get(orderId) ?? createdOrders.find((o) => o.id === orderId);
      if (!order) throw new Error('Shipment not found');
      return structuredClone(shipmentFromOrder(withPatch(order)));
    },

    listRoutes: async (businessId, date) => {
      if (!isPrimary(businessId)) return fallback.listRoutes(businessId, date);
      try {
        const [orders] = await Promise.all([fetchOrders({ deliveryDate: date }), fetchRoutes()]);
        const stops = orders.filter((o) => o.status !== 'cancelled').map(shipmentFromOrder);
        const byRoute = new Map<string, Shipment[]>();
        for (const s of stops) {
          if (!s.routeId) continue;
          byRoute.set(s.routeId, [...(byRoute.get(s.routeId) ?? []), s]);
        }
        return [...byRoute.entries()].map(([routeId, routeStops]) => {
          const info = routeCatalog.get(routeId);
          const delivered = routeStops.filter((s) => s.status === 'delivered').length;
          const moving = routeStops.some((s) => s.status === 'en-route');
          return {
            ...audit(new Date().toISOString()),
            id: routeId,
            businessId: businessId as Route['businessId'],
            custom: {},
            name: info?.name ?? `Route ${routeId.slice(-4)}`,
            date,
            vehicleId: info?.vehicle ?? null,
            // The feed gives a driver name, not a user id; the sheet
            // resolves unknown ids to themselves, so the name shows.
            driverId: (info?.driver ?? null) as Route['driverId'],
            carrierPartyId: null,
            status:
              delivered === routeStops.length && routeStops.length > 0
                ? ('completed' as const)
                : moving
                  ? ('dispatched' as const)
                  : ('planning' as const),
            departsAt: null,
            returnedAt: null,
            shipmentIds: routeStops.map((s) => s.id),
            note: null,
          };
        });
      } catch (err) {
        console.warn('[legacy] listRoutes fell back to seed:', err);
        return fallback.listRoutes(businessId, date);
      }
    },

    listProducts: async (businessId, params = {}) => {
      if (!isPrimary(businessId)) return fallback.listProducts(businessId, params);
      try {
        await ensureFeed();
      } catch {
        return fallback.listProducts(businessId, params);
      }
      let rows = [...productCatalog.values()];
      if (params.search) {
        const q = params.search.trim().toLowerCase();
        rows = rows.filter((p) =>
          [p.sku, ...Object.values(p.name)].some((s) => s?.toLowerCase().includes(q)),
        );
      }
      return rows.sort((a, b) => (a.name['en'] ?? '').localeCompare(b.name['en'] ?? ''));
    },

    getProduct: async (businessId, productId) => {
      if (!isPrimary(businessId)) return fallback.getProduct(businessId, productId);
      try {
        await ensureFeed();
      } catch {
        /* serve what we have */
      }
      return productCatalog.get(productId) ?? null;
    },

    listParties: async (businessId, params = {}) => {
      if (!isPrimary(businessId)) return fallback.listParties(businessId, params);
      try {
        await ensureFeed();
      } catch {
        return fallback.listParties(businessId, params);
      }
      const suppliers = await fetchSuppliers();
      let rows = [...customerCatalog.values(), ...suppliers];
      if (params.role) rows = rows.filter((p) => p.roles.includes(params.role as never));
      if (params.search) {
        const q = params.search.trim().toLowerCase();
        rows = rows.filter((p) =>
          [p.code, p.name].some((s) => s.toLowerCase().includes(q)),
        );
      }
      return rows.sort((a, b) => a.name.localeCompare(b.name));
    },

    getParty: async (businessId, partyId) => {
      if (!isPrimary(businessId)) return fallback.getParty(businessId, partyId);
      try {
        await ensureFeed();
      } catch {
        /* serve what we have */
      }
      return (
        customerCatalog.get(partyId) ??
        (await fetchSuppliers()).find((p) => p.id === partyId) ??
        null
      );
    },

    /* The money, stock and people tabs stay on the seed until their
       endpoints are wired — one screen, one source, no half-real data. */
    listStock: (businessId, params) => fallback.listStock(businessId, params),
    listInvoices: (businessId, params) => fallback.listInvoices(businessId, params),
    listPayments: (businessId, params) => fallback.listPayments(businessId, params),
    listBalances: (businessId, direction) => fallback.listBalances(businessId, direction),
    listThreads: (businessId, params) => fallback.listThreads(businessId, params),
    listMessages: (businessId, threadId) => fallback.listMessages(businessId, threadId),
    sendMessage: (businessId, threadId, body, channel) =>
      fallback.sendMessage(businessId, threadId, body, channel),
    listAppointments: (businessId, from, to) => fallback.listAppointments(businessId, from, to),
    listTeam: (businessId) => fallback.listTeam(businessId),
  };
};
